if (!customElements.get('spirit-product-gallery')) {
  class SpiritProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.counter = this.querySelector('[data-gallery-counter]');
      this.previousTrackButton = this.querySelector('[data-gallery-scroll-prev]');
      this.nextTrackButton = this.querySelector('[data-gallery-scroll-next]');
      this.openers = Array.from(this.querySelectorAll('[data-gallery-open]'));
      this.templates = Array.from(this.querySelectorAll('[data-gallery-template]'));
      this.activeIndex = 0;
      this.trackIndex = 0;
      this.lastTrigger = null;
      this.scrollFrame = null;

      this.previousTrackButton?.addEventListener('click', () => this.scrollTrack(-1));
      this.nextTrackButton?.addEventListener('click', () => this.scrollTrack(1));
      this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.showRelative(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showRelative(1));
      this.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.dialog?.close());

      this.openers.forEach((opener, index) => {
        opener.addEventListener('click', () => this.open(index, opener));
      });

      this.track?.addEventListener(
        'scroll',
        () => {
          this.classList.add('spirit-event-gallery--has-scrolled');
          this.requestTrackSync();
        },
        { passive: true }
      );
      this.resizeObserver = new ResizeObserver(() => this.syncTrackState());
      if (this.track) this.resizeObserver.observe(this.track);

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.dialog?.addEventListener('close', () => this.onClose());
      this.dialog?.addEventListener('keydown', (event) => this.onKeydown(event));

      requestAnimationFrame(() => this.syncTrackState());
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    open(index, opener) {
      if (!this.dialog || !this.viewport) return;

      this.lastTrigger = opener;
      this.activeIndex = index;
      this.renderActive();

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
        this.querySelector('[data-gallery-close]')?.focus();
        if (opener?.hasAttribute('data-gallery-autoplay')) {
          const video = this.viewport.querySelector('video');
          const playback = video?.play();
          playback?.catch(() => {});
          const iframe = this.viewport.querySelector('iframe');
          if (iframe?.src) {
            const url = new URL(iframe.src, window.location.href);
            if (['www.youtube.com', 'www.youtube-nocookie.com', 'player.vimeo.com'].includes(url.hostname)) {
              url.searchParams.set('autoplay', '1');
              url.searchParams.set('playsinline', '1');
              iframe.src = url.href;
            }
          }
        }
      }
    }

    showRelative(direction) {
      if (this.templates.length < 2) return;

      this.activeIndex = (this.activeIndex + direction + this.templates.length) % this.templates.length;
      this.renderActive();
    }

    renderActive() {
      const template = this.templates[this.activeIndex];
      if (!template || !this.viewport) return;

      this.stopMedia();
      this.viewport.replaceChildren(template.content.cloneNode(true));

      if (this.counter) {
        this.counter.textContent = `${this.activeIndex + 1} / ${this.templates.length}`;
      }

      const hideNavigation = this.templates.length < 2;
      this.querySelectorAll('[data-gallery-previous], [data-gallery-next]').forEach((button) => {
        button.hidden = hideNavigation;
      });
    }

    scrollTrack(direction) {
      if (!this.track || !this.openers.length) return;

      const trackRect = this.track.getBoundingClientRect();
      const candidates = this.openers
        .map((opener) => ({ opener, rect: opener.getBoundingClientRect() }))
        .filter(({ rect }) =>
          direction < 0 ? rect.left < trackRect.left - 2 : rect.right > trackRect.right + 2
        )
        .sort((first, second) =>
          direction < 0 ? second.rect.right - first.rect.right : first.rect.left - second.rect.left
        );
      const target = candidates[0]?.opener;
      if (!target) return;

      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
      this.updateTrackButtons();
    }

    requestTrackSync() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.syncTrackState();
      });
    }

    syncTrackState() {
      if (!this.track || !this.openers.length) return;

      const trackRect = this.track.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const anchor = rtl ? trackRect.right : trackRect.left;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.openers.forEach((opener, index) => {
        const rect = opener.getBoundingClientRect();
        const edge = rtl ? rect.right : rect.left;
        const distance = Math.abs(edge - anchor);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.trackIndex = closestIndex;
      this.updateTrackButtons();
    }

    updateTrackButtons() {
      if (!this.track || !this.openers.length) return;
      const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 2;
      const trackRect = this.track.getBoundingClientRect();
      const canScrollLeft = hasOverflow && this.openers.some((opener) => opener.getBoundingClientRect().left < trackRect.left - 2);
      const canScrollRight = hasOverflow && this.openers.some((opener) => opener.getBoundingClientRect().right > trackRect.right + 2);

      if (this.previousTrackButton) {
        this.previousTrackButton.hidden = !hasOverflow;
        this.previousTrackButton.disabled = !hasOverflow || !canScrollLeft;
        this.previousTrackButton.setAttribute('aria-disabled', String(this.previousTrackButton.disabled));
      }
      if (this.nextTrackButton) {
        this.nextTrackButton.hidden = !hasOverflow;
        this.nextTrackButton.disabled = !hasOverflow || !canScrollRight;
        this.nextTrackButton.setAttribute('aria-disabled', String(this.nextTrackButton.disabled));
      }
    }

    onKeydown(event) {
      const rtl = getComputedStyle(this).direction === 'rtl';
      const mediaHasFocus = event.target instanceof HTMLMediaElement;
      if (event.key === 'ArrowLeft') {
        if (mediaHasFocus) return;
        event.preventDefault();
        this.showRelative(rtl ? 1 : -1);
      } else if (event.key === 'ArrowRight') {
        if (mediaHasFocus) return;
        event.preventDefault();
        this.showRelative(rtl ? -1 : 1);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.dialog?.close();
      }
    }

    onClose() {
      this.stopMedia();
      this.viewport?.replaceChildren();
      this.lastTrigger?.focus();
    }

    stopMedia() {
      this.viewport?.querySelectorAll('video').forEach((video) => video.pause());
    }
  }

  customElements.define('spirit-product-gallery', SpiritProductGallery);
}

if (!customElements.get('spirit-provider-rail')) {
  class SpiritProviderRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-provider-rail-track]');
      this.items = Array.from(this.querySelectorAll('[data-provider-rail-item]'));
      this.previousButton = this.querySelector('[data-provider-rail-previous]');
      this.nextButton = this.querySelector('[data-provider-rail-next]');
      this.activeIndex = 0;
      this.scrollFrame = null;

      this.previousButton?.addEventListener('click', () => this.scrollToRelative(-1));
      this.nextButton?.addEventListener('click', () => this.scrollToRelative(1));
      this.track?.addEventListener('scroll', () => this.requestSync(), { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.sync());
      if (this.track) this.resizeObserver.observe(this.track);
      requestAnimationFrame(() => this.sync());
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    scrollToRelative(direction) {
      if (!this.track || !this.items.length) return;

      const trackRect = this.track.getBoundingClientRect();
      const candidates = this.items
        .map((item) => ({ item, rect: item.getBoundingClientRect() }))
        .filter(({ rect }) =>
          direction < 0 ? rect.left < trackRect.left - 2 : rect.right > trackRect.right + 2
        )
        .sort((first, second) =>
          direction < 0 ? second.rect.right - first.rect.right : first.rect.left - second.rect.left
        );
      const target = candidates[0]?.item;
      if (!target) return;

      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
      this.updateButtons();
    }

    requestSync() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.sync();
      });
    }

    sync() {
      if (!this.track || !this.items.length) return;
      const trackRect = this.track.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const anchor = rtl ? trackRect.right : trackRect.left;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      this.items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const edge = rtl ? rect.right : rect.left;
        const distance = Math.abs(edge - anchor);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      this.activeIndex = closestIndex;
      this.updateButtons();
    }

    updateButtons() {
      if (!this.track) return;
      const hasOverflow = this.track.scrollWidth > this.track.clientWidth + 2;
      const trackRect = this.track.getBoundingClientRect();
      const canScrollLeft = hasOverflow && this.items.some((item) => item.getBoundingClientRect().left < trackRect.left - 2);
      const canScrollRight = hasOverflow && this.items.some((item) => item.getBoundingClientRect().right > trackRect.right + 2);
      if (this.previousButton) {
        this.previousButton.hidden = !hasOverflow;
        this.previousButton.disabled = !hasOverflow || !canScrollLeft;
        this.previousButton.setAttribute('aria-disabled', String(this.previousButton.disabled));
      }
      if (this.nextButton) {
        this.nextButton.hidden = !hasOverflow;
        this.nextButton.disabled = !hasOverflow || !canScrollRight;
        this.nextButton.setAttribute('aria-disabled', String(this.nextButton.disabled));
      }
    }
  }

  customElements.define('spirit-provider-rail', SpiritProviderRail);
}
